import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Eye, Building2, UserCheck, Target, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { renderMarkdownContent } from '../../utils/insightsRenderers';

interface InsightsModalProps {
  lead: any;
  onClose: () => void;
}

function parseInsightsSections(raw: string): { company: string; decisionMaker: string; outreachAngles: string; personalizationHooks: string } {
  const result = { company: '', decisionMaker: '', outreachAngles: '', personalizationHooks: '' };
  if (!raw) return result;

  const cleaned = raw.replace(/\*\*Summary:\*\*[\s\S]*$/, '').trim();

  const isMarkdownFormat = /##\s*\d+[\.\)]/.test(cleaned);

  if (isMarkdownFormat) {
    const parts = cleaned.split(/##\s*\d+[\.\)]\s*/);
    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const lower = trimmed.toLowerCase();
      const stripTitle = (pattern: RegExp) => trimmed.replace(pattern, '').replace(/^\([^)]*\)\s*/i, '').replace(/^\n+/, '');
      if (lower.startsWith('decision maker insights') || lower.startsWith('person insights')) {
        result.decisionMaker = stripTitle(/^(?:decision maker|person) insights\s*/i);
      } else if (lower.startsWith('company insights')) {
        result.company = stripTitle(/^company insights\s*/i);
      } else if (lower.startsWith('outreach angles')) {
        result.outreachAngles = stripTitle(/^outreach angles\s*/i);
      } else if (lower.startsWith('personalization hooks') || lower.startsWith('personal hooks')) {
        result.personalizationHooks = stripTitle(/^(?:personalization|personal) hooks\s*/i);
      }
    }
  } else {
    const headerPositions: { key: string; index: number }[] = [];
    const headerRegex = /^(?:\d+[\.\)]\s*)?(DECISION MAKER INSIGHTS|PERSON INSIGHTS|COMPANY INSIGHTS|OUTREACH ANGLES|PERSONALIZATION HOOKS|PERSONAL HOOKS)\s*:?/gim;
    let match;
    while ((match = headerRegex.exec(cleaned)) !== null) {
      headerPositions.push({ key: match[1].toLowerCase(), index: match.index });
    }

    for (let i = 0; i < headerPositions.length; i++) {
      const start = cleaned.indexOf('\n', headerPositions[i].index);
      if (start === -1) continue;
      const end = i + 1 < headerPositions.length ? headerPositions[i + 1].index : cleaned.length;
      const content = cleaned.substring(start, end).trim();
      const key = headerPositions[i].key;

      if (key.includes('decision maker') || key === 'person insights') {
        result.decisionMaker = content;
      } else if (key.includes('company')) {
        result.company = content;
      } else if (key.includes('outreach')) {
        result.outreachAngles = content;
      } else if (key.includes('personalization') || key === 'personal hooks') {
        result.personalizationHooks = content;
      }
    }
  }

  return result;
}


const InsightsModal: React.FC<InsightsModalProps> = ({ lead, onClose }) => {
  const [insightsText, setInsightsText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        setIsLoading(true);
        const leadId = lead.lead_id || lead.id;

        const { data, error } = await supabase
          .from('leads_tracking')
          .select('lead_insights')
          .eq('lead_id', leadId)
          .maybeSingle();

        if (error) throw error;
        setInsightsText(data?.lead_insights || null);
      } catch (err) {
        console.error('Failed to fetch insights data:', err);
        setError('Failed to load insights data');
      } finally {
        setIsLoading(false);
      }
    };

    if (lead.lead_id || lead.id) {
      fetchInsights();
    }
  }, [lead]);

  const sections = insightsText ? parseInsightsSections(insightsText) : null;

  const boxes: { title: string; icon: React.FC<any>; iconColor: string; content: string }[] = sections
    ? [
        { title: 'Company Insights', icon: Building2, iconColor: 'text-[#1875FF]', content: sections.company },
        { title: 'Decision Maker Insights', icon: UserCheck, iconColor: 'text-[#05E0D1]', content: sections.decisionMaker },
        { title: 'Outreach Angles', icon: Target, iconColor: 'text-[#1875FF]', content: sections.outreachAngles },
        { title: 'Personalization Hooks', icon: Sparkles, iconColor: 'text-[#05E0D1]', content: sections.personalizationHooks },
      ]
    : [];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/20 animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
              <Eye className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Lead Insights</h3>
              <p className="text-xs text-gray-500 font-medium">{lead.dm_full_name ? `${lead.dm_full_name} - ` : ''}{lead.company_name || 'Unknown Company'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#1875FF] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 text-sm font-medium">{error}</div>
            </div>
          ) : !sections || (!sections.company && !sections.decisionMaker && !sections.outreachAngles && !sections.personalizationHooks) ? (
            <div className="text-center py-12">
              <Eye size={32} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 text-sm font-medium">No insights available for this lead yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {boxes.map((box) => {
                const Icon = box.icon;
                return (
                  <div
                    key={box.title}
                    className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-5 shadow-sm hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Icon size={16} className={box.iconColor} />
                      <h4 className="text-sm font-bold text-gray-900 font-display">{box.title}</h4>
                    </div>
                    <div className="max-h-80 overflow-y-auto pr-1 custom-scrollbar">
                      {renderMarkdownContent(box.content)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InsightsModal;
